// ============================================
// NurseOrchestrator — The Missing Glue
//
// Boots all 10 NRS services and wires their
// callbacks to form a working pipeline:
//   Captain → NRS.01 → NRS.03 → NRS.05/06 → NRS.08 → Protocol
//
// Exposes ChatDataSource-compatible methods:
//   nurseRespond(), detectLateReveal(), getModifiers()
//
// Constructor: sessionId, patientId, sex, age,
//   reproductiveStatus, box (PatientBox)
// ============================================

import type { PatientBox } from '../chat/liveDataSource';
import type { ReproductiveStatus } from '../../../functions/src/nurse/types/triageSession';
import type { LabResultInjection } from '../../../functions/src/nurse/types/captainResultListener';
import type { ModalType, ModalSubmission, ModalSubmissionData } from '../../../functions/src/nurse/types/intakeModalOrchestration';
import type { DetectedDisclosure } from '../../../functions/src/nurse/types/chatDisclosureDetection';
import type { NurseResponse, ModalMarker } from '../../../functions/src/nurse/types/conversationEngine';
import type { CaptainResponse } from '../../types/captainPipeline';
import type { ModifierSet as PipelineModifierSet } from '../../types/patientJourney';

// Service imports — the 10 NRS modules
import { CaptainResultListenerService } from '../../../functions/src/nurse/services/captainResultListener';
// NRS.02 is pure functions (not imported here — triage done before orchestrator boots)
import { ConversationEngineService, extractMarker } from '../../../functions/src/nurse/services/conversationEngine';
import { filterNurseOutput, createDriftState } from '../../../functions/src/nurse/services/safetyOutputFilter';
import { ModalOrchestratorService } from '../../../functions/src/nurse/services/modalOrchestrator';
import { ChatDisclosureService } from '../../../functions/src/nurse/services/chatDisclosureService';
// NRS.07: explanation engine — called on demand via presentLabResults()
// XMO.09 D#3 c4: explainCorrectedResults re-runs pipeline on corrected values
import { presentLabResults, explainCorrectedResults } from '../../../functions/src/nurse/services/resultExplanationEngine';
// NRS.10: test harness not wired at runtime
import { SessionHandoffService, createSessionHandoff } from '../../../functions/src/nurse/services/sessionHandoff';
import { ConversationAuditService } from '../../../functions/src/nurse/services/conversationAudit';

// Adapter imports
import { createFirestoreProvider, createSessionFirestoreProvider, createAuditFirestoreProvider } from './firestoreAdapters';
import { createGeminiLLMAdapter } from './geminiLLMAdapter';
import { safetyProfileToModifiers } from './safetyProfileToModifiers';

// XMO.01 D#3: Prompt injection filter (pattern matching only — no LLM in security loop)
import { scanForInjection, buildInjectionAuditLog, INJECTION_PATIENT_MESSAGE } from '../xmod/promptInjectionFilter';

import type { GeminiMessage } from '../../services/ai/providers/gemini';

// ── Types ──

export interface NurseOrchestratorConfig {
  sessionId: string;
  patientId: string;
  sex: 'male' | 'female';
  age?: number;
  reproductiveStatus: ReproductiveStatus;
  box: PatientBox;
}

export interface OrchestratorState {
  phase: 'pre-analysis' | 'mid-analysis' | 'post-triage';
  labsInjected: boolean;
  protocolTriggered: boolean;
  turnNumber: number;
}

// ── Orchestrator ──

export class NurseOrchestrator {
  // NRS services
  private captainListener: CaptainResultListenerService;
  private conversationEngine: ConversationEngineService;
  private modalOrchestrator: ModalOrchestratorService;
  private disclosureService: ChatDisclosureService;
  private sessionHandoff: SessionHandoffService;
  private auditService: ConversationAuditService;

  // State
  private driftState;
  private state: OrchestratorState;
  private lastCaptainResponse: CaptainResponse | null = null;
  // XMO.01 D#3: session ID stored for injection audit logging
  private readonly sessionId: string;
  // NRS.01 D#1 c3: Bound handlers for cleanup on navigation/timeout
  private boundBeforeUnload: (() => void) | null = null;
  private boundVisibilityChange: (() => void) | null = null;
  private sessionTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  // NRS.01 D#1: Session timeout in ms (30 minutes)
  private static readonly SESSION_TIMEOUT_MS = 30 * 60 * 1000;

  constructor(config: NurseOrchestratorConfig) {
    this.sessionId = config.sessionId;

    // Build adapters
    const firestoreProvider = createFirestoreProvider();
    const sessionFirestore = createSessionFirestoreProvider();
    const auditFirestore = createAuditFirestoreProvider();
    const llmProvider = createGeminiLLMAdapter(config.sessionId, config.patientId);

    // NRS.01: Captain Result Listener
    this.captainListener = new CaptainResultListenerService(
      { patientId: config.patientId, sessionId: config.sessionId },
      firestoreProvider,
    );

    // NRS.03: Conversation Engine
    this.conversationEngine = new ConversationEngineService(
      config.sessionId,
      config.patientId,
      llmProvider,
    );

    // NRS.04: Safety filter state
    this.driftState = createDriftState(config.sessionId, 'nurse');

    // NRS.05: Modal Orchestrator
    this.modalOrchestrator = new ModalOrchestratorService(config.sex);

    // NRS.06: Chat Disclosure Service
    this.disclosureService = new ChatDisclosureService();

    // NRS.08: Session Handoff
    this.sessionHandoff = createSessionHandoff(
      config.sessionId,
      config.patientId,
      sessionFirestore,
    );
    this.sessionHandoff.setTriageData(config.sex, config.age);

    // NRS.09: Audit
    this.auditService = new ConversationAuditService(auditFirestore);

    // Initialize state
    this.state = {
      phase: 'pre-analysis',
      labsInjected: false,
      protocolTriggered: false,
      turnNumber: 0,
    };

    // Wire NRS.03 filter function using NRS.04
    this.conversationEngine.setFilterFunction((text: string) => {
      const result = filterNurseOutput(text, this.driftState);
      this.driftState = result.driftState;

      // NRS.04 violations → NRS.09 audit trail (fire-and-forget)
      if (result.violations.length > 0) {
        this.auditService.flagForReview(
          config.sessionId,
          config.patientId,
          result,
          'nurse',
          result.passed ? 'flagged' : 'blocked',
          this.state.turnNumber,
        );
      }

      return { passed: result.passed, text: result.text };
    });
  }

  /**
   * NRS.01 D#1 c1: Initialize — boot services, wire callbacks, check for session gap.
   * Call ONCE before first interaction. Session MUST exist (sessionId validated in constructor).
   * Subscribes to captain results only after session is confirmed.
   */
  async init(): Promise<void> {
    // Wire NRS.01 callback chain — NRS.01 D#4: Route by extractionStatus
    this.captainListener.onLabResultInjection((injection: LabResultInjection) => {
      this.lastCaptainResponse = injection.captainResponse;

      try {
        this.routeByExtractionStatus(injection);
      } catch (err) {
        console.error('[NurseOrchestrator] Status routing failed:', err);
      }
    });

    this.captainListener.onListenerError((error: string) => {
      console.error('[NRS.01] Listener error:', error);
    });

    // NRS.01 D#1 c4: Wire health alert callback
    this.captainListener.onHealthAlertCallback((alert) => {
      console.error('[NRS.01] Health alert:', alert.reason, {
        sessionId: alert.sessionId,
        consecutiveDisconnects: alert.consecutiveDisconnects,
        lastDisconnectAt: alert.lastDisconnectAt.toISOString(),
      });
    });

    // XMO.09 D#3 c4: Wire corrections callback — re-run NRS.07 explanation pipeline on corrected values
    this.captainListener.onCorrectionAppliedCallback((correctedResponse, correction) => {
      this.lastCaptainResponse = correctedResponse;

      // c4: Re-run NRS.07 explanation pipeline with corrected values
      const context = this.conversationEngine.getContext();
      const correctedNames = [correction.biomarkerName];
      explainCorrectedResults(
        correctedResponse,
        context.collectedGoals,
        context.collectedConcerns,
        correctedNames,
      );
    });

    // NRS.01 D#1 c1: Start listener (session already validated — start subscribes to Firestore)
    this.captainListener.start();

    // NRS.01 D#1 c3: Wire navigation/tab close cleanup
    this.wireLifecycleCleanup();

    // NRS.01 D#1 c3: Start session timeout (resets on each interaction)
    this.resetSessionTimeout();

    // Check for session gap (page refresh / tab return)
    try {
      await this.sessionHandoff.checkForGap('page_load');
    } catch (err) {
      console.error('[NRS.08] Gap check failed:', err);
    }
  }

  /**
   * Process a user message through the full NRS pipeline.
   * Returns the nurse response text (post-filter).
   */
  async nurseRespond(
    _history: GeminiMessage[],
    systemPrompt: string,
    userMsg: string,
  ): Promise<string> {
    this.state.turnNumber++;
    const turn = this.state.turnNumber;

    // XMO.01 D#3 (c1): Scan for prompt injection BEFORE forwarding to Nurse AI
    const injectionResult = scanForInjection(userMsg);
    if (injectionResult.blocked) {
      console.warn('[XMO.01]', buildInjectionAuditLog(injectionResult, this.sessionId));
      return INJECTION_PATIENT_MESSAGE;
    }

    // NRS.03: set system prompt and send
    this.conversationEngine.setSystemPrompt(systemPrompt);
    const response: NurseResponse = await this.conversationEngine.send(userMsg);

    // Check for modal markers (NRS.05)
    const { marker } = extractMarker(response.text);
    if (marker) {
      this.handleMarker(marker);
    }

    // Check for [BUILD_PROTOCOL] — trigger completion event
    if (response.text.includes('[BUILD_PROTOCOL]') || marker === '[BUILD_PROTOCOL]') {
      this.state.protocolTriggered = true;
      this.emitCompletion(turn).catch(err =>
        console.error('[NRS.08] Completion event failed:', err),
      );
    }

    // NRS.08: persist state (fire-and-forget)
    this.sessionHandoff.touchActive().catch(err =>
      console.error('[NRS.08] touchActive failed:', err),
    );

    // NRS.01 D#1 c3: Reset session timeout on each interaction
    this.resetSessionTimeout();

    return response.text;
  }

  /**
   * Scan user message for late disclosures (NRS.06).
   * Returns detected disclosures for the caller to handle.
   */
  detectLateReveal(msg: string, turn: number): DetectedDisclosure[] {
    const disclosures = this.disclosureService.scan(msg, turn);

    // Update NRS.08 safety profile from chat disclosures
    for (const disclosure of disclosures) {
      const mergeField = disclosureTypeToField(disclosure.type);
      if (mergeField) {
        this.sessionHandoff.updateFromChat(
          mergeField,
          disclosure.matchedKeywords,
          turn,
        );
      }
    }

    return disclosures;
  }

  /**
   * Process modal submission (NRS.05 → NRS.08).
   */
  processModalSubmission(modalType: ModalType, data: ModalSubmissionData): void {
    const submission: ModalSubmission = {
      modalType,
      action: 'submit',
      data,
      submittedAt: new Date(),
    };

    const flags = this.modalOrchestrator.processSubmission(submission);

    // Update NRS.08 with modal data
    if (data.type === 'medications') {
      this.sessionHandoff.updateFromModal('medications', data.medications);
    } else if (data.type === 'allergies') {
      this.sessionHandoff.updateFromModal('allergies', data.allergies);
    } else if (data.type === 'pregnancy') {
      this.sessionHandoff.updatePregnancyStatus(
        data.isPregnant,
        data.isTryingToConceive,
        data.isBreastfeeding,
      );
    }

    // Sync completeness flags
    this.sessionHandoff.updateDataCompletenessFlags(flags);

    // Record in conversation engine
    if (data.type === 'goals') {
      this.conversationEngine.recordCollection({ goals: data.goals });
    } else if (data.type === 'medications') {
      this.conversationEngine.recordCollection({ meds: data.medications });
    } else if (data.type === 'allergies') {
      this.conversationEngine.recordCollection({ allergies: data.allergies });
    } else if (data.type === 'pregnancy') {
      this.conversationEngine.recordCollection({ isPregnant: data.isPregnant });
    }
  }

  /**
   * Scan a user message for disclosures (NRS.06).
   * Convenience method for the chat engine.
   */
  scanForDisclosures(text: string, turn: number): DetectedDisclosure[] {
    return this.detectLateReveal(text, turn);
  }

  /**
   * Should a modal be shown for a given marker?
   * NRS.05 handles sex-gating + dedup.
   */
  shouldShowModal(marker: string): { show: boolean; modalType: ModalType | null } {
    const modalType = this.modalOrchestrator.detectModal(marker);
    if (!modalType) return { show: false, modalType: null };
    return { show: this.modalOrchestrator.shouldShowModal(modalType), modalType };
  }

  /**
   * Build real ModifierSet from NRS.08 SafetyDataProfile.
   * Replaces EMPTY_MODIFIER_SET in the live path.
   */
  getModifiers(): PipelineModifierSet {
    const profile = this.sessionHandoff.getSafetyProfile();
    return safetyProfileToModifiers(profile);
  }

  /**
   * Emit NRS.08 completion event → triggers protocol generation.
   */
  async emitCompletion(turn?: number): Promise<void> {
    const turnNumber = turn ?? this.state.turnNumber;
    await this.sessionHandoff.emitCompletionEvent(turnNumber);
  }

  /**
   * Get current orchestrator state.
   */
  getState(): Readonly<OrchestratorState> {
    return { ...this.state };
  }

  /**
   * Get the last Captain response (if labs have been injected).
   */
  getCaptainResponse(): CaptainResponse | null {
    return this.lastCaptainResponse;
  }

  /**
   * NRS.01 D#1 c3: Stop all listeners and clean up.
   * Call on unmount, session end, or navigation away.
   */
  stop(): void {
    this.captainListener.stop('session_end');
    this.unwireLifecycleCleanup();
    if (this.sessionTimeoutTimer) {
      clearTimeout(this.sessionTimeoutTimer);
      this.sessionTimeoutTimer = null;
    }
  }

  // ── Private: Lifecycle (NRS.01 D#1) ──

  /**
   * NRS.01 D#1 c3: Wire browser events for clean unsubscribe on navigation away.
   */
  private wireLifecycleCleanup(): void {
    // beforeunload — tab/window close
    this.boundBeforeUnload = () => {
      this.captainListener.stop('navigation_away');
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', this.boundBeforeUnload);
    }

    // visibilitychange — tab hidden for extended period
    this.boundVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Don't stop immediately — the user might come back quickly.
        // The session timeout will handle extended absence.
      }
      if (document.visibilityState === 'visible' && !this.captainListener.isActive()) {
        // Tab returned but listener died — restart
        try {
          this.captainListener.start();
        } catch (err) {
          console.error('[NRS.01] Failed to restart listener on tab return:', err);
        }
      }
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.boundVisibilityChange);
    }
  }

  /** Remove browser event listeners */
  private unwireLifecycleCleanup(): void {
    if (this.boundBeforeUnload) {
      if (typeof window !== 'undefined') {
        window.removeEventListener('beforeunload', this.boundBeforeUnload);
      }
      this.boundBeforeUnload = null;
    }
    if (this.boundVisibilityChange) {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', this.boundVisibilityChange);
      }
      this.boundVisibilityChange = null;
    }
  }

  /**
   * NRS.01 D#1 c3: Reset session timeout. Called on init and on each interaction.
   * After SESSION_TIMEOUT_MS of inactivity, listener is cleanly unsubscribed.
   */
  private resetSessionTimeout(): void {
    if (this.sessionTimeoutTimer) {
      clearTimeout(this.sessionTimeoutTimer);
    }
    this.sessionTimeoutTimer = setTimeout(() => {
      console.warn('[NRS.01] Session timeout — unsubscribing listener');
      this.captainListener.stop('timeout');
    }, NurseOrchestrator.SESSION_TIMEOUT_MS);
  }

  // ── Private: Status Routing (NRS.01 D#4) ──

  /**
   * NRS.01 D#4: Route lab result injection by extractionStatus.
   * - complete: NRS.03 injectLabResults() → NRS.07 presentLabResults() sequentially
   * - partial: same as complete with isPartial: true (completeness indicator)
   * - failed: NRS.03 injectLabResults() with failure context; empathetic message
   * - unknown: log error, treat as failed
   */
  private routeByExtractionStatus(injection: LabResultInjection): void {
    const status = injection.extractionStatus;

    switch (status) {
      case 'complete': {
        // c1: NRS.03 inject → NRS.07 present (sequential)
        this.conversationEngine.injectLabResults({
          captainResponse: injection.captainResponse,
          extractionStatus: 'complete',
          isPartial: false,
        });
        this.conversationEngine.transitionPhase('mid-analysis', 'lab_results_injected');
        this.state.phase = 'mid-analysis';
        this.state.labsInjected = true;

        // NRS.07: present results (no partial flag)
        const context = this.conversationEngine.getContext();
        presentLabResults(
          injection.captainResponse,
          context.collectedGoals,
          context.collectedConcerns,
        );
        break;
      }

      case 'partial': {
        // c2: Same as complete but isPartial: true → completeness indicator
        this.conversationEngine.injectLabResults({
          captainResponse: injection.captainResponse,
          extractionStatus: 'partial',
          isPartial: true,
        });
        this.conversationEngine.transitionPhase('mid-analysis', 'lab_results_injected');
        this.state.phase = 'mid-analysis';
        this.state.labsInjected = true;

        // NRS.07: present results with partial flag
        const partialContext = this.conversationEngine.getContext();
        presentLabResults(
          injection.captainResponse,
          partialContext.collectedGoals,
          partialContext.collectedConcerns,
          { isPartial: true },
        );
        break;
      }

      case 'failed': {
        // c3 + c4: NRS.03 inject with failure context; Nurse generates empathetic message
        this.conversationEngine.injectLabResults({
          extractionStatus: 'failed',
          failureReason: injection.failureReason,
        });
        this.conversationEngine.transitionPhase('mid-analysis', 'lab_results_injected');
        this.state.phase = 'mid-analysis';
        this.state.labsInjected = true;
        // XMO.03 owns retake flow — no presentLabResults() call for failed
        break;
      }

      default: {
        // c5: Unknown extractionStatus — log error, treat as failed
        console.error(
          `[NRS.01 D#4] Unknown extractionStatus: "${status}" — treating as failed`,
          { sessionId: injection.sessionId, patientId: injection.patientId },
        );
        this.conversationEngine.injectLabResults({
          extractionStatus: 'failed',
          failureReason: `Unknown extraction status: ${status}`,
        });
        this.conversationEngine.transitionPhase('mid-analysis', 'lab_results_injected');
        this.state.phase = 'mid-analysis';
        this.state.labsInjected = true;
        break;
      }
    }
  }

  // ── Private ──

  private handleMarker(marker: ModalMarker): void {
    const modalType = this.modalOrchestrator.detectModal(marker);
    if (modalType) {
      this.modalOrchestrator.markModalShown(modalType);
    }
  }

  /**
   * Persist session state to Firestore.
   * Called after significant state changes.
   */
  async persistState(): Promise<void> {
    const modifiers = this.getModifiers();
    await this.sessionHandoff.persist(
      modifiers as unknown as Record<string, unknown>,
      [], // late reveals tracked separately
      this.state.phase,
      this.state.turnNumber,
      this.state.labsInjected,
      this.state.protocolTriggered,
    );
  }
}

// ── Helpers ──

function disclosureTypeToField(
  type: string,
): 'medications' | 'allergies' | 'conditions' | null {
  switch (type) {
    case 'MEDICATION': return 'medications';
    case 'ALLERGY': return 'allergies';
    case 'CONDITION': return 'conditions';
    default: return null;
  }
}

// ── Factory ──

export function createNurseOrchestrator(
  config: NurseOrchestratorConfig,
): NurseOrchestrator {
  return new NurseOrchestrator(config);
}
